import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InvestmentAccessService } from '@common/services/investment/investment-access.service';
import { ROUTE_PATHS } from '../../utils/route-paths.const';

/**
 * Guards entry into the Investment Flow against pending/rejected blocking
 * statuses from the Alerts API — covers manual URL, deep links, and browser
 * navigation that the imperative "Invest Now" gate can't intercept.
 *
 * When blocked, opens the blocking-status popup and redirects HOME (same
 * navigate + return-false pattern the other investment guards use for cold
 * visits). Fails open on an alerts error so a transient outage can't trap
 * users — the in-flow server eligibility check remains the safety net.
 *
 * Composed BEFORE `investmentGuard`, which owns the orthogonal
 * popstate/completed-flag concern.
 */
export const investmentAccessGuard: CanActivateFn = (): Observable<boolean> => {
  const access = inject(InvestmentAccessService);
  const router = inject(Router);

  return access.ensureFreshThenIsBlocked().pipe(
    map(blocked => {
      if (!blocked) return true;
      access.openBlockedPopup();
      router.navigate([ROUTE_PATHS.HOME], {
        replaceUrl: true,
        state: { bypassGuard: true },
      });
      return false;
    }),
    catchError(() => of(true))
  );
};
